import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import { BlogService } from 'src/app/blog.service';
import { MetaSetterService } from 'src/app/general/meta-setter.service';
import { SportDataService } from 'src/app/sport-data.service';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-golf-rankings-page',
	templateUrl: './golf-rankings-page.component.html',
	styleUrls: ['./golf-rankings-page.component.scss']
})
export class GolfRankingsPageComponent implements OnInit {
	golfRankingsLoading = true;
	golfRankingsError = false;
	golfRankingsSort = "rank";
	golfRankings = [];
	displayedGolfRankings = [];

	trustedDesc: SafeHtml;
	trustedCopy: SafeHtml;

	environment = environment;

	countryLookup = {
		"ALBANIA": "ALB",
		"ARGENTINA": "ARG",
		"AUSTRALIA": "AUS",
		"AUSTRIA": "AUT",
		"BAHAMAS": "BHS",
		"BAHRAIN": "BHR",
		"BANGLADESH": "BGD",
		"BELGIUM": "BEL",
		"BERMUDA": "BMU",
		"BOTSWANA": "BWA",
		"BRAZIL": "BRA",
		"CANADA": "CAN",
		"CAYMAN ISLANDS": "CYM",
		"CHILE": "CHI",
		"CHINA": "CHN",
		"CHINESE TAIPEI": "TWN",
		"COLOMBIA": "COL",
		"COSTA RICA": "CRI",
		"CZECH REPUBLIC": "CZE",
		"DENMARK": "DEN",
		"DOMINICAN REPUBLIC": "DOM",
		"ECUADOR": "ECU",
		"ENGLAND": "ENG",
		"ESTONIA": "EST",
		"FIJI": "FIJ",
		"FINLAND": "FIN",
		"FRANCE": "FRA",
		"GERMANY": "DEU",
		"GUATEMALA": "GTM",
		"HONG KONG": "HKG",
		"ICELAND": "ISL",
		"INDIA": "IND",
		"INDONESIA": "IDN",
		"IRELAND": "IRL",
		"ISLE OF MAN": "IMN",
		"ITALY": "ITA",
		"JAPAN": "JPN",
		"JORDAN": "JOR",
		"KENYA": "KEN",
		"MALAYSIA": "MYS",
		"MAURITIUS": "MUS",
		"MEXICO": "MEX",
		"MONACO": "MCO",
		"MOROCCO": "MAR",
		"MYANMAR": "MMR",
		"NAMIBIA": "NAM",
		"NETHERLANDS": "NED",
		"NEW ZEALAND": "NZL",
		"NORTHERN IRELAND": "NIR",
		"NORWAY": "NOR",
		"OMAN": "OMN",
		"PAKISTAN": "PAK",
		"PANAMA": "PAN",
		"PARAGUAY": "PAR",
		"PHILIPPINES": "PHL",
		"POLAND": "POL",
		"PORTUGAL": "PRT",
		"PUERTO RICO": "PRI",
		"QATAR": "QAT",
		"SAUDI ARABIA": "SAU",
		"SCOTLAND": "SCO",
		"SERBIA": "SRB",
		"SINGAPORE": "SGP",
		"SLOVAKIA": "SVK",
		"SOUTH AFRICA": "RSA",
		"SOUTH KOREA": "KOR",
		"SPAIN": "ESP",
		"SRI LANKA": "LKA",
		"SWAZILAND": "SWZ",
		"SWEDEN": "SWE",
		"SWITZERLAND": "CHE",
		"TAIWAN": "TWN",
		"TANZANIA": "TZA",
		"THAILAND": "THA",
		"TURKEY": "TUR",
		"UGANDA": "UGA",
		"UNITED ARAB EMIRATES": "ARE",
		"UNITED STATES": "USA",
		"USA": "USA",
		"VENEZUELA": "VEN",
		"VIETNAM": "VNM",
		"WALES": "WAL",
		"ZAMBIA": "ZMB",
		"ZIMBABWE": "ZIM",
	};

	faSortDesc = faSortDown;
	faSortAsc = faSortUp;
	faSort = faSort;

	constructor(
		private metaSetter: MetaSetterService,
		private sportDataService: SportDataService,
		private blogService: BlogService,
		private sanitiser: DomSanitizer,
	) { }

	ngOnInit(): void {
		this.blogService.getAppSettings("golf-rankings")
			.subscribe(settings => {
				this.metaSetter.setMeta({
					title: settings.app_title,
					seoTitle: settings.seo_title,
					description: settings.seo_description || settings.app_description,
					metaImageURL: environment.defaultMetaImage,
					canonicalURL: environment.canonicalDomain + "/sport-hub/golf/rankings",
				});

				this.trustedDesc = this.sanitiser.bypassSecurityTrustHtml(settings.app_page_subheader);
				this.trustedCopy = this.sanitiser.bypassSecurityTrustHtml(settings.subheading_description);
			}, () => {
				this.metaSetter.setMeta({
					title: environment.siteTitle,
					description: environment.defaultMetaDescription,
					metaImageURL: environment.defaultMetaImage,
					canonicalURL: environment.canonicalDomain + "/sport-hub/golf/rankings",
				})
			});

		this.getGolfRankings();
	}

	golfRankingsSortToggle(variable) {
		if (this.golfRankingsSort === variable) {
			this.golfRankingsSort = "-" + variable;
		} else {
			this.golfRankingsSort = variable;
		}

		this.calculateDisplayedGolfRankings();
	}

	getGolfRankings() {
		this.golfRankingsLoading = true;
		this.golfRankingsError = false;

		if (typeof document !== "undefined") {
			this.sportDataService.getGolfRankingsData()
				.subscribe(rankingsData => {
					this.golfRankings = rankingsData;
					this.calculateDisplayedGolfRankings();
					this.golfRankingsLoading = false;
				}, () => {
					this.golfRankingsError = true;
				})
		}
	}

	matchCountry(code: string) {
		if (Object.keys(this.countryLookup).includes(code)) {
			return this.countryLookup[code];
		}

		return "";
	}

	calculateDisplayedGolfRankings() {
		const sortVariable = this.golfRankingsSort.match(/([^\-]+$)/g)[0];
		this.displayedGolfRankings = this.golfRankings
			.slice()
			.sort((a,b) => {

				if (typeof a[sortVariable] !== "number" && typeof b[sortVariable] !== "number") {
					return 0;
				}
	
				if (typeof a[sortVariable] !== "number") {
					return 1;
				}
	
				if (typeof b[sortVariable] !== "number") {
					return -1;
				}
	
				if (this.golfRankingsSort[0] === "-") {
					return b[sortVariable] - a[sortVariable]
				} else {
					return a[sortVariable] - b[sortVariable]
				}
			})
			.slice(0,100);
	}

}
